import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, FormLabel } from "react-bootstrap";
import Dropdown, { Option } from "react-dropdown";
import { LoadingOverlay, Loader } from "react-overlay-loader";

import {
    AuthenticationMethodsLabel,
    ComposeMesssageConstants,
    getAuthMethodTitle,
} from "../../Core/Common/Constants";
import { initialSentMessage, ISentMessageModel } from "../../Core/ViewModels/Message/SentMessageModel";
import {
    initialSentMessageRecipientModel,
    ISentMessageRecipientModel,
} from "../../Core/ViewModels/Message/SentMessageRecipientModel";
import { IRecipientModel } from "../../Core/ViewModels/Message/RecipientModel";
import { ISentMailMessage } from "../../Core/ViewModels/Message/SentMailMessageModel";
import { IFileModel } from "../../Core/ViewModels/File/FileModel";
import { QnAActions } from "../../reducers/recipientQuestionAnswerReducer";
import {
    AuthenticationMethod,
    DropOffLinkType,
    MessageActions,
    RecipientType,
    SendMethodWindow,
    SettingMode,
    ValidationType,
} from "src/Core/Common/Enums";
import { AppNotifier } from "../Common/Notification/AppNotifier";
import {
    fetchAddInEmailTemplate,
    fetchAuthRetentionPeriods,
    fetchPrimaryAdmin,
    saveDocuments,
    saveSentMessageToStore,
    sendMessage,
} from "../../actionCreators/composeActionCreators";
import { IValidationMessage, validateComposeMessage } from "../../helper/Validations";
import Recipient from "src/components/Parts/Recipient";
import { ShieldIcon } from "src/assets/images/svg/IconCollection";
import { ApplicationState } from "src/store";
import {
    getAuthenticationMethodDialogDimensions,
    getFileName,
    getFormattedRetentionString,
    getRetentionOptions,
} from "../../helper/HelperFunctions";
import {
    fetchAuthenticationMethodSettings,
    fetchContactList,
    fetchOnehubLink,
    fetchRetentionSettings,
    fetchSupportContactSettings,
} from "src/actionCreators/adminSettingsActionCreators";
import useForceUpdate from "src/Core/Utilities/hooks/useForceUpdate";
import { getAddInEmailBodyTemplate, getDefaultTaxYearOptions } from "./Helper/HelperFunctions";
import { ISecurityQuestionAnswer } from "src/Core/Domain/Compose/IComposeModel";
import { IRetentionPeriod } from "src/Core/ViewModels/Company/Settings/RetentionSettings";
import { fetchMessageSecurityQuestions } from "src/actionCreators/securityQuestionCreators";
import { RecipientValidationActions } from "src/reducers/recipientValidationReducers";
import { logger } from "src/routes/LoggedIn";
import { appInsightsData } from "src/helper/AppInsightsData";
import { ISupportContact } from "src/reducers/adminSettingsReducer";
import { requestDropOffLinks } from "src/actionCreators/dropOffActionCreators";
import { downloadCompanyLogo } from "src/actions/attachmentActions";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { fetchDefaultSavedMailTemplateText } from "src/actionCreators/SavedMessageActionCreator";
import { CustomModal } from "../Common/Modal/CustomModal";
import LexicalEditor from "../Common/ui/LexicalEditor/LexicalEditor";
import { DefaultTaxYearConstants } from "src/helper/Constants";
interface IComposeProps {
    showCompose: boolean;
    messageAction: MessageActions;
    handleOnContinueClick: () => void;
    handleOnBackClick: () => void;
    isMounted: boolean;
}

const Compose: React.FC<IComposeProps> = props => {
    const dispatch = useDispatch();
    const userSettingsStore = useSelector((state: ApplicationState) => state.userSettingsState);
    const sentMessageFromStore = useSelector((state: ApplicationState) => state.composeState.sentMessage);
    const { messageOptions: messageOptionsSettings } = useSelector(
        (state: ApplicationState) => state.adminSettingsState
    );
    const composeState = useSelector((state: ApplicationState) => state.composeState);
    const securityQuestionState = useSelector((state: ApplicationState) => state.securityQuestionState);
    const profile = useSelector((state: ApplicationState) => state.userProfile);
    const recipients = useSelector((state: ApplicationState) => state.recipients);
    const recipientValidationState = useSelector((state: ApplicationState) => state.recipientValidationState);
    const forceUpdate = useForceUpdate();

    const loggedinUserProfile = useSelector((state: ApplicationState) => state.userAuth.user.profile);
    const allowEmployeesToChangeAuthMethod = useSelector(
        (state: ApplicationState) =>
            state.adminSettingsState.messageOptions.authenticationSettings.allowEmployeesToChangeAuthMethod
    );

    const oneHubLink = useSelector((state: ApplicationState) => state.adminSettingsState.oneHubLink);

    const [confirmSend, setConfirmSend] = React.useState({
        show: false,
        sendAnyway: false,
        message: "",
    });
    const [attachments, setAttachments] = React.useState<IFileModel[]>([]);
    const [sentMessage, setSentMessage] = React.useState<ISentMessageModel>(sentMessageFromStore);
    const [subject, setSubject] = React.useState<string>("");
    const [isAuthMethodUpdated, setIsAuthMethodUpdated] = React.useState(false);
    const [companyLogoUrl, setCompanyLogoUrl] = React.useState("");
    const [isRetentionUpdated, setIsRetentionUpdated] = React.useState(false);
    const [selectedAuthMethod, setSelectedAuthMethod] = React.useState<AuthenticationMethod>(
        sentMessageFromStore?.authenticationMethod
    );
    const [supportConactInfo, setSupportContactInfo] = React.useState<ISupportContact>({
        firstName: "",
        lastName: "",
        emailAddress: "",
        userId: 0,
        phoneNumber: "",
    });
    const { supportContactList, supportContactSettings, isLoading } = useSelector(
        (state: ApplicationState) => state.adminSettingsState
    );
    const dropOffLinks = useSelector((state: ApplicationState) => state.dropOffState.dropOffLinks);
    const dropOffLinkType = userSettingsStore && userSettingsStore.userSettings && userSettingsStore.userSettings.dropOffSettings?.dropOffLinkType;
    const dropOffLinkToShowInEmail = (dropOffLinkType && dropOffLinkType === DropOffLinkType.PERSONAL) ? dropOffLinks.personalDropOffLink :
        dropOffLinkType === DropOffLinkType.COMPANY ? dropOffLinks.companyDropOffLink : null;
    const [mailText, setMailText] = React.useState<string>("");

    const dropOffLinkString = dropOffLinkToShowInEmail
        ? `Send file(s) back using this <a class="text-link" href="${dropOffLinkToShowInEmail}">link</a>`
        : "";

    const [selectedTaxYear, setSelectedTaxYear] = React.useState<number>();

    const taxYearsOptions = React.useMemo(() => {
        return getDefaultTaxYearOptions();
    }, []);

    const getSelectedTaxYearOption = (): Option | string => {
        const selectedValue = selectedTaxYear?.toString();
        return taxYearsOptions.find((ty) => ty.value == selectedValue) ?? "";
    };
    React.useEffect(() => {
        composeInitiate();
    }, [composeState.documents.length]);

    React.useEffect(() => {
        if (!props.isMounted && !isAuthMethodUpdated) {
            getAuthDefaultRetentionPeriods(
                userSettingsStore?.userSettings?.authenticationMethodSettings.defaultAuthenticationMethod
            );
        }
        dispatch(fetchAuthenticationMethodSettings());
        dispatch(fetchAddInEmailTemplate(ComposeMesssageConstants.EMAIL_TEMPLATE_NAME));
        const fetchSettingsFunction = () => dispatch(fetchSupportContactSettings());
        dispatch(fetchContactList(0, fetchSettingsFunction));
        dispatch(downloadCompanyLogo((url: string) => {
            setCompanyLogoUrl(url);
        }));
        dispatch(requestDropOffLinks());
        dispatch(fetchOnehubLink());
        fetchDefaultSavedMailTemplateText(handleMailTextFetchSuccess);
    }, []);

    React.useEffect(() => {
        if (!props.isMounted) {
            dispatch(saveSentMessageToStore({
                ...sentMessageFromStore,
                message: initialSentMessage.message,
                notifyWhenDownloaded: initialSentMessage.notifyWhenDownloaded
            }));
        }
        getSelectedTaxYear();

    }, []);

    React.useEffect(() => {
        if (userSettingsStore.userSettings && !isAuthMethodUpdated && !props.isMounted) {
            const methodOptions = messageOptionsSettings.authenticationSettings.authenticationMethods;
            const userDefaultMethod =
                userSettingsStore.userSettings.authenticationMethodSettings.defaultAuthenticationMethod;
            const adminDefaultMethod = messageOptionsSettings.authenticationSettings.defaultAuthenticationMethod;
            const allowUserToChange = messageOptionsSettings.authenticationSettings.allowEmployeesToChangeDefaultMethod;

            if (methodOptions.includes(userDefaultMethod) && allowUserToChange) {
                setSelectedAuthMethod(userDefaultMethod);
            } else {
                setSelectedAuthMethod(adminDefaultMethod);
            }
            setSentMessage({
                ...sentMessage,
                authenticationMethod:
                    userSettingsStore?.userSettings?.authenticationMethodSettings.defaultAuthenticationMethod || 0,
                expiresDays: getRetentionPeriodFromUserSettings(
                    userSettingsStore.userSettings.authenticationMethodSettings.defaultAuthenticationMethod
                ),
            });
        }
    }, [userSettingsStore?.userSettings?.authenticationMethodSettings, messageOptionsSettings.authenticationSettings]);

    React.useEffect(() => {
        if (supportContactSettings) {
            const defaultContact = supportContactList.find(
                data => data.userId === supportContactSettings.supportContactId
            );
            if (!defaultContact) getPrimaryAdminForCompany();
            else setSupportContactInfo(defaultContact);
        }
    }, [supportContactSettings]);

    React.useEffect(() => {
        confirmSend.sendAnyway && handleOnSend();
    }, [confirmSend.show]);

    const getPrimaryAdminForCompany = () => {
        dispatch(fetchPrimaryAdmin((data: ISupportContact) => setSupportContactInfo(data)));
    };

    const composeInitiate = () => {
        dispatch(fetchRetentionSettings());
        getSubjectFromItem();
        dispatch(fetchMessageSecurityQuestions(SettingMode.BOTH));
        if (composeState?.documents.length > 0) {
            let convertedAttachments: IFileModel[] = [];
            convertedAttachments = composeState.documents.map(item => {
                return {
                    fileId: 0,
                    fileGuid: item.guid,
                    isUploadComplete: item.isUploaded,
                    name: getFileName(item.file.name ?? item.name),
                    size: item.file.size ?? item.size,
                    uploadProgress: item.progressBar * 100,
                };
            });
            setAttachments(convertedAttachments);
        }
    };

    const getAuthDefaultRetentionPeriods = (authMethod: AuthenticationMethod) => {
        dispatch(
            fetchAuthRetentionPeriods(authMethod, (data: IRetentionPeriod[]) =>
                setSentMessage({
                    ...sentMessage,
                    authenticationMethod: authMethod,
                    expiresDays: data.find(x => x.isDefault).days,
                })
            )
        );
    };

    const getSubjectFromItem = () => {
        let subject = "";
        Office.context.mailbox.item.subject.getAsync(asyncResult => {
            subject = asyncResult.value;
            setSubject(subject);
            setSentMessage({
                ...sentMessage,
            });
        });
    };

    const getRetentionPeriodFromUserSettings = (authenticationMethod: AuthenticationMethod) => {
        if (!profile) return 0;

        switch (authenticationMethod) {
            case AuthenticationMethod.ACCESSCODE:
                return getSelectedOption(
                    userSettingsStore?.userSettings?.retentionSettings.defaultRetentionPeriods.accessCode
                );
            case AuthenticationMethod.QUESTIONANSWER:
                return getSelectedOption(
                    userSettingsStore?.userSettings?.retentionSettings.defaultRetentionPeriods.questionAndAnswer
                );
            // case AuthenticationMethod.LOGINPASSWORD:
            //     return getSelectedOption(
            //         userSettingsStore?.userSettings?.retentionSettings.defaultRetentionPeriods.loginTransfer
            //     );
            default:
                return 0;
        }
    };
    const getSelectedTaxYear = (): void => {
        let selectedTaxYear: number;
        if (userSettingsStore.userSettings?.taxYearSettings?.defaultTaxYear) {
            selectedTaxYear = userSettingsStore.userSettings?.taxYearSettings?.defaultTaxYear;
        }
        else {
            const currentDate = new Date();
            const mayFirst = new Date(currentDate.getFullYear(), 4, 1); // Month is 0-based, so 4 = May
            selectedTaxYear = currentDate >= mayFirst ? currentDate.getFullYear() : currentDate.getFullYear() - 1;
        }
        setSelectedTaxYear(selectedTaxYear);
    };
    const getSelectedOption = (value?: number) => {
        if (value) {
            const result = composeState.authRetentions.data.find(rp => rp.retentionId === value);

            if (result) return result.days;
            else return 0;
        }
        return 0;
    };

    const toggleAuthMethodDialog = (windowType: SendMethodWindow) => {
        setIsAuthMethodUpdated(true);
        const url = `${window.location.origin}/compose/auth-method?selectedAuthMethod=${selectedAuthMethod}&windowType=${windowType}`;
        Office.context.ui.displayDialogAsync(
            url,
            getAuthenticationMethodDialogDimensions(),
            handleAuthMedhodDialogCallback
        );
    };

    const handleAuthMedhodDialogCallback = asyncResult => {
        const dialog = asyncResult.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: { message: string; origin: string }) => {
            if (arg.message === "close") {
                dialog.close();
            } else {
                handleAuthMethodDialogMessageReceivedCallback(arg, dialog);
            }
        });
    };

    const handleAuthMethodDialogMessageReceivedCallback = (arg, dialog) => {
        dialog.close();
        const message = JSON.parse(arg.message);

        if (message.type === AuthenticationMethod.QUESTIONANSWER) {
            dispatch({
                type: RecipientValidationActions.UPDATE,
                payload: { ...recipientValidationState, isQnARecipientValid: true },
            });
            dispatch({ type: QnAActions.SET, payload: message.payload });
        }
        const authenticationMethodFromDialog = message.type as AuthenticationMethod;
        if (selectedAuthMethod !== authenticationMethodFromDialog) {
            setSelectedAuthMethod(message.type as AuthenticationMethod);
            if (!isRetentionUpdated) {
                setSentMessage({
                    ...sentMessage,
                    authenticationMethod: authenticationMethodFromDialog,
                    expiresDays: getRetentionPeriodFromUserSettings(authenticationMethodFromDialog),
                });
            }
        } else {
            setSentMessage({
                ...sentMessage,
                authenticationMethod: authenticationMethodFromDialog
            });
        }
        forceUpdate();
    };

    const handleSubjectChange = (value: string) => {
        setSubject(value);
        Office.context.mailbox.item.subject.setAsync(value);
    };

    const handleMessageBodyChange = (value: string) => {
        setSentMessage((prevState: ISentMessageModel) => ({
            ...prevState,
            message: value,
        }));
    };

    const handleChangeRetention = (e: Option) => {
        const tempMessage: ISentMessageModel = {
            ...sentMessage,
            expiresDays: +e.value,
        };
        setSentMessage(tempMessage);
        setIsRetentionUpdated(true);
        logger.trackEvent({
            name: `${appInsightsData.Compose.pageTitle} - ${appInsightsData.Compose.Operation.RetentionPeriodChanged}`,
            properties: {
                page: appInsightsData.Compose.pageTitle,
                retentionPeriod: e.value,
            },
        });
    };

    const handleOnChangeTaxYear = (option: any) => {
        setSelectedTaxYear(option.value);
    };

    const handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const tempMessage: ISentMessageModel = {
            ...sentMessage,
            notifyWhenDownloaded: e.currentTarget.checked === true ? 1 : 0,
        };
        setSentMessage(tempMessage);
    };

    const handleOnSend = () => {
        if (
            selectedAuthMethod === AuthenticationMethod.QUESTIONANSWER
            &&
            !recipientValidationState.isQnARecipientValid
        ) {
            toggleAuthMethodDialog(SendMethodWindow.QUESTION_ANSWER);
        } else {
            validateMessageAndSend();
        }
    };

    const validateMessageAndSend = () => {
        const sentRecepients: ISentMessageRecipientModel[] = [];
        const questionAnswersForRecipients: ISecurityQuestionAnswer[] = [];

        recipients.forEach((item: IRecipientModel) => {
            sentRecepients.push({
                ...initialSentMessageRecipientModel,
                firstName: item.firstName,
                lastName: item.lastName,
                email: item.emailAddress,
                recipientType: item.recipientType,
                expiresDays: sentMessage.expiresDays,
                recipientId: item.userId ?? 0,
                securityQuestion: item?.question?.trim() ?? "",
                securityAnswer: item?.answer?.trim() ?? "",
            });

            if (selectedAuthMethod === AuthenticationMethod.QUESTIONANSWER) {
                questionAnswersForRecipients.push({
                    securityQuestionId: (!item.questionId || item.questionId === 0)
                        ? securityQuestionState.securityQuestions.data.find(x => x.isDefault)?.messageSecurityQuestionId
                        : item.questionId,
                    answer: item?.answer?.trim() ?? "",
                    recipientEmail: item.emailAddress,
                    question: item?.question?.trim() ?? "",
                });

            }
        });

        const message: ISentMailMessage = {
            messageDetail: {
                ...sentMessage, authenticationMethod: selectedAuthMethod, subject: subject?.trim(),
                taxYear: selectedTaxYear
            },
            recipients: sentRecepients,
            attachments: attachments,
            recipientSecurityQuestions: questionAnswersForRecipients,
        };

        const validationMessage: IValidationMessage = validateComposeMessage(message);

        switch (validationMessage.validationType) {
            case ValidationType.WARNING:
                if (!confirmSend.sendAnyway) {
                    setConfirmSend({ show: true, sendAnyway: false, message: validationMessage.message });
                } else {
                    dispatch(sendMessage(message, result => handleSuccess(result), handleError));
                }
                break;
            case ValidationType.INVALID:
                AppNotifier.Warning(validationMessage.message);
                break;
            case ValidationType.VALID:
                if (!recipientValidationState.isRecipientValid) {
                    AppNotifier.Warning(ToasterMessages.WARNING.UNSAVED_MESSAGE_OPTIONS);
                } else {
                    dispatch(sendMessage(message, result => handleSuccess(result), handleError));
                }
                break;
        }
    };

    const handleSuccess = (link: string) => {
        if (link) {
            appendLinkToItemMessage(link);
        }
    };

    const handleError = () => {
        AppNotifier.Error(ToasterMessages.ERROR.SEND_MESSAGE_FAILED);
        setAttachments([]);
        dispatch(saveDocuments([]));
        props.handleOnBackClick();
    };

    const appendLinkToItemMessage = link => {
        const newCompanyLogoUrl = `<img src=${companyLogoUrl} alt="company-logo" />`;
        const template = getAddInEmailBodyTemplate(
            loggedinUserProfile.company_name,
            loggedinUserProfile.given_name,
            loggedinUserProfile.family_name,
            loggedinUserProfile.email,
            link,
            sentMessage.expiresDays.toString(),
            sentMessage.expiresDays ? " Kindly download them before " : "",
            attachments,
            supportConactInfo,
            newCompanyLogoUrl,
            dropOffLinkString,
            composeState.addInEmailTemplate.data,
            oneHubLink,
            mailText
        );
        Office.context.mailbox.item.body.prependAsync(
            `${template} <br /><br />`,
            { coercionType: Office.CoercionType.Html },
            asyncResult => {
                setAttachments([]);
                dispatch({
                    type: RecipientValidationActions.UPDATE,
                    payload: { isQnARecipientValid: false, isRecipientValid: false },
                });
                dispatch(saveDocuments([], () => Office.context.ui.closeContainer()));
                dispatch(saveSentMessageToStore(initialSentMessage));
            }
        );
    };

    const handleOnHideConfirmSend = () => {
        setConfirmSend({ show: false, sendAnyway: false, message: "" });
    };

    const backClickHandler = () => {
        props.handleOnBackClick();
        dispatch(saveSentMessageToStore(sentMessage));
    };
    const handleMailTextFetchSuccess = (data: string) => {
        setMailText(data);
    };
    return (
        <>
            {props.showCompose && (
                <Form className="compose-form-wrapper" onSubmit={e => e.preventDefault()}>
                    <Recipient toRecipients={recipients.filter(x => x.recipientType === RecipientType.ToRecipient)} />

                    <Form.Group className="compose-msg-form-group">
                        <FormLabel>{ComposeMesssageConstants.SUBJECT_LABEL}</FormLabel>
                        <Form.Control
                            type="text"
                            placeholder={ComposeMesssageConstants.SUBJECT_PLACEHOLDER}
                            value={subject}
                            onChange={event => handleSubjectChange(event.target.value)}
                            maxLength={ComposeMesssageConstants.SUBJECT_MAX_LENGTH}
                        />
                    </Form.Group>
                    <Form.Group className="compose-msg-form-group message-wrapper">
                        <FormLabel>{ComposeMesssageConstants.MESSAGE_LABEL}</FormLabel>
                        <LexicalEditor
                            messageBody={sentMessage.message}
                            changeStateEditorBody={e => handleMessageBodyChange(e)}
                        />
                    </Form.Group>
                    <div className="flex-row auth-method-wrapper">
                        <div className="flex-row auth-text-img-wrapper">
                            <FormLabel>Authentication Method</FormLabel>
                            <div
                                style={{
                                    cursor: !allowEmployeesToChangeAuthMethod ? "not-allowed" : "pointer",
                                }}
                                title={AuthenticationMethodsLabel.CHANGE_AUTHENTICATION_METHOD_TOOLTIP}
                                className="shieldIcon"
                                onClick={() =>
                                    allowEmployeesToChangeAuthMethod &&
                                    toggleAuthMethodDialog(SendMethodWindow.METHOD_SELECT)
                                }
                            >
                                <ShieldIcon />
                            </div>
                        </div>
                        <FormLabel>{getAuthMethodTitle(selectedAuthMethod)}</FormLabel>
                    </div>
                    <div className="flex-row download-select-wrapper">
                        <FormLabel>{ComposeMesssageConstants.TAX_YEAR_TEXT}</FormLabel>
                        <Dropdown
                            options={taxYearsOptions}
                            onChange={handleOnChangeTaxYear}
                            placeholder={DefaultTaxYearConstants.Tax_YEAR_PLACEHOLDER_TEXT}
                            controlClassName="retention-dropdown"
                            value={getSelectedTaxYearOption()}
                        />
                        <Loader classNamePrefix="sm" text="" loading={composeState?.authRetentions?.isLoading} />
                    </div>

                    <div className="flex-row download-select-wrapper">
                        <FormLabel>{ComposeMesssageConstants.FILES_DOWNLOAD_TEXT}</FormLabel>

                        <Dropdown
                            className={`${!messageOptionsSettings.retentionSettings?.isUserAllowedToChangeMessageAndDRRetentionPeriod ? "disabled" : ""}`}
                            options={
                                composeState?.authRetentions && !composeState.authRetentions.isLoading
                                    ? getRetentionOptions(composeState.authRetentions.data)
                                    : []
                            }
                            onChange={handleChangeRetention}
                            placeholder="Select Retention Period"
                            controlClassName="retention-dropdown"
                            value={getFormattedRetentionString(sentMessage?.expiresDays)}
                            disabled={!messageOptionsSettings.retentionSettings?.isUserAllowedToChangeMessageAndDRRetentionPeriod}
                        />
                        <Loader classNamePrefix="sm" text="" loading={composeState?.authRetentions?.isLoading} />
                    </div>
                    <div className="footer-btns">
                        <Button variant="secondary" onClick={backClickHandler}>
                            Back
                        </Button>
                        <Button variant="primary" onClick={handleOnSend}>
                            Finish
                        </Button>
                    </div>
                </Form>
            )}
            <CustomModal
                show={confirmSend.show}
                title="Alert"
                onSubmit={() => setConfirmSend({ show: false, sendAnyway: true, message: "" })}
                confirmButtonName="Send Anyway"
                cancelButtonName="Don't Send"
                onHide={handleOnHideConfirmSend}
                isConfirmModal={true}
            >
                {confirmSend.message}
            </CustomModal>
            <LoadingOverlay className="overlay-position">
                <Loader classNamePrefix="sm" text="Sending Message..." loading={composeState?.isSending} />
            </LoadingOverlay>
        </>
    );
};

export default Compose;